/**
 * Collapsed Submenu Popover
 * Shows a flyout menu when sidebar is collapsed
 */

import { useState } from 'react'
import { Link, useLocation, useTranslation } from '@ufc/core'
import { cn, Popover, PopoverContent, PopoverTrigger } from '@ufc/uikit'
import type { CollapsedSubmenuProps } from '../types'

// ============================================
// Helper Functions
// ============================================

function getPopoverSide(isRTL: boolean): 'left' | 'right' {
  return isRTL ? 'left' : 'right'
}

function getButtonStyles(isActive: boolean): string {
  const baseStyles = 'w-full flex items-center justify-center p-3 rounded-xl text-sm font-medium transition-colors duration-200'
  const activeStyles = 'bg-primary/10 dark:bg-primary/20 text-primary'
  const inactiveStyles = 'text-muted-foreground hover:bg-secondary hover:text-foreground'

  return cn(baseStyles, isActive ? activeStyles : inactiveStyles)
}

function getMenuItemStyles(isActive: boolean): string {
  const baseStyles = 'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200'
  const activeStyles = 'bg-primary text-primary-foreground'
  const inactiveStyles = 'text-muted-foreground hover:bg-secondary hover:text-foreground'

  return cn(baseStyles, isActive ? activeStyles : inactiveStyles)
}

// ============================================
// Component
// ============================================

export function CollapsedSubmenu({
  icon: Icon,
  label,
  items,
  isActive,
  isRTL,
  onNavigate,
}: CollapsedSubmenuProps) {
  const { t } = useTranslation()
  const location = useLocation()
  const [open, setOpen] = useState(false)

  const handleNavigate = () => {
    setOpen(false)
    onNavigate?.()
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className={getButtonStyles(isActive)}>
          <Icon className="w-5 h-5 flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={getPopoverSide(isRTL)}
        align="start"
        sideOffset={8}
        className="w-56 p-2 bg-white dark:bg-slate-900 border border-border shadow-lg rounded-xl"
      >
        <div className="mb-2 px-2 py-1.5">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {label}
          </span>
        </div>
        <div className="space-y-1">
          {items.map((item) => {
            const isItemActive = location.pathname === item.to
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={handleNavigate}
                className={getMenuItemStyles(isItemActive)}
              >
                <item.icon className="w-4 h-4" />
                <span className="flex-1 text-start truncate">{t(item.labelKey)}</span>
              </Link>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
